import { useMarkets } from "../lib/useMarkets";
import { loadRuntimeConfig, marketAvailable, marketIdentity } from "../lib/runtimeConfig";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { timing } from "../config";
import {
  api,
  ApiError,
  DEMO_MAKER,
  DEMO_SIGNATURE,
  parseQuoteDraft,
  type LopOrder,
  type Quote,
} from "../lib/api";
import {
  formatBaseUnits,
  formatBaseUnitsExact,
  fromBaseUnits,
  isPositiveAmount,
  toBaseUnits,
} from "../lib/amount";
import { getAddress } from "../lib/auth";
import { cancelIntent, CancelError } from "../lib/cancelIntent";
import { buildOrderTypedData, EIP712_DOMAIN_TYPE } from "../lib/contracts";
import type { EditIntent } from "../lib/editIntent";
import {
  approve,
  isReverted,
  MAX_UINT256,
  readAllowance,
  waitForReceipt,
} from "../lib/erc20";
import { errorReporter } from "../lib/errorReporter";
import {
  formatCountdown,
  formatRate,
  formatUsd,
  secondsUntil,
  shortHash,
} from "../lib/format";
import { getChainId, getLopAddress, getMaxTicketUsd } from "../lib/runtimeConfig";
import {
  decimalsBySymbol,
  defaultPair,
  isFiatLeg,
  isUsdPegged,
  tokenAddress,
  tokenBySymbol,
} from "../lib/tokens";
import {
  useOrderOutcome,
  type OrderOutcome,
  type OutcomeFill,
} from "../lib/useOrderOutcome";
import { useTokenBalances } from "../lib/useTokenBalances";
import {
  explainWalletError,
  isUserRejected,
  signTypedData,
  toWalletError,
  type Hex,
} from "../lib/wallet";
import { AmountShortcuts } from "./AmountShortcuts";
import { DemoChip } from "./DemoChip";
import { InlineTxLink, TxLink } from "./ExplorerLink";
import { DEFAULT_SLIPPAGE_BPS, SlippageControl } from "./SlippageControl";
import { TokenSelect } from "./TokenSelect";

interface TradeCardProps {
  onOrderSubmitted: () => void;
  /** An order being edited: it prefills the card, and submitting cancels it on chain first. */
  editing?: EditIntent | null;
  /** Called once the replacement is in: the edit context is spent. */
  onEditReplaced?: () => void;
}

/** Why the gateway refused the intent — drives the banner and the approve step. */
type SubmitFailure =
  | "balance"
  | "allowance"
  | "maker"
  | "email"
  | "signature"
  | "structure"
  | "generic";

/**
 * Which leg the user fixed. The other input is derived: every quote overwrites
 * it and a change to its counterpart clears it, so only one box is the intent.
 */
type ExactSide = "sell" | "buy";

/**
 * A quote with both base-unit legs pinned at quote time: an LOP order fixes
 * makingAmount and takingAmount, so submit never mixes a freshly typed input
 * with a stale quoted counterpart.
 */
interface LiveQuote {
  marketIdentity: string;
  quote: Quote;
  sellToken: string;
  buyToken: string;
  side: ExactSide;
  /** makingAmount — base units of the sell (maker) asset. */
  sellBase: string;
  /** takingAmount — base units of the buy (taker) asset. */
  buyBase: string;
  /** Part of the quote's identity: it moves the floor, so a quote at 0.5% does not answer 1%. */
  slippageBps: number;
}

/** One pending quote fetch. A new object always means "quote again". */
interface QuoteRequest {
  sellToken: string;
  buyToken: string;
  side: ExactSide;
  /** Base units of the fixed leg. Never empty and never zero — see requestQuote. */
  base: string;
  slippageBps: number;
}

/** Characters a decimal amount field accepts (separators tolerated). */
const AMOUNT_PATTERN = /^[\d,\s']*\.?\d*$/;

/** A base-unit leg from the wire, or null. Numbers are accepted because gateways have sent both. */
function baseLeg(value: unknown): string | null {
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 ? String(value) : null;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return /^\d+$/.test(trimmed) ? trimmed : null;
}

/**
 * EUR/USD are display-only reference legs: the gateway would store the signed
 * order, which then sits open forever because its address is a placeholder.
 */
function fiatLegExplanation(symbol: string): string {
  return `${symbol} is a quote-only reference leg on this testnet — pick an on-chain token (EURC, USDC, USDT, TRYB, BRZ, IDRX) to settle.`;
}

/**
 * The one button's machine: idle → approving? → signing → checking? → submitting
 * → success, or rejected, priceMoved or error. `approving` is skipped when the
 * allowance covers the sell leg, `signing` for a demo session, `checking` unless
 * the quote expired in the wallet. Touching an input returns any end state to idle.
 */
type FlowState =
  | { kind: "idle" }
  /** Editing: invalidating the original on chain before anything is signed. */
  | { kind: "cancelling"; hash: Hex | null }
  /** `hash` appears once the approval is in the mempool. */
  | { kind: "approving"; hash: Hex | null }
  | { kind: "signing" }
  /** Re-quoting after signing, because the signed quote had expired. */
  | { kind: "checking" }
  | { kind: "submitting" }
  /** The pair and rate ride along: the card blanks on success, and the banner still names them. */
  | { kind: "success"; orderHash: string; sellToken: string; buyToken: string; quotedRate: number }
  /** The user declined in their wallet — expected, never reported. */
  | { kind: "rejected"; step: "approve" | "sign" | "cancel" }
  /** Signed one price, the market has another. Both are shown; nothing is sent. */
  | { kind: "priceMoved"; signed: string; fresh: string }
  /** A required contract address has not arrived from runtime config. */
  | { kind: "unavailable" }
  /** `detail` is the gateway's own problem-json explanation, never dropped. */
  | { kind: "error"; message: string; reason: SubmitFailure; detail?: string };

/** A token leg or the LOP is not in the descriptor yet: a guessed address signs invalid orders. */
const ADDRESS_UNAVAILABLE = "Contract address unavailable — try again shortly";

const balanceFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Why a `POST /api/v1/orders` was refused, so the banner says what to do. A 400
 * is not always a signature problem: a structural refusal is a 400 too, and
 * calling it a bad signature makes the user re-sign a good order.
 */
function classifySubmitFailure(err: unknown): SubmitFailure {
  if (!(err instanceof ApiError)) return "generic";
  // `message` carries the raw body (with `title`) for gateways that word `detail` differently.
  const text = `${err.detail ?? ""} ${err.message}`.toLowerCase();
  if (err.status === 403 && text.includes("email")) return "email";
  if (err.status === 403) return "maker";
  if (err.status === 400) {
    // Only a genuine signature verdict maps to the signature message.
    return text.includes("invalid signature") || text.includes("signature does not")
      ? "signature"
      : "structure";
  }
  if (err.status === 422) {
    if (text.includes("allowance") || text.includes("approve")) return "allowance";
    if (text.includes("balance") || text.includes("holds")) return "balance";
  }
  return "generic";
}

function submitFailureMessage(reason: SubmitFailure, sellSymbol: string, held: string): string {
  switch (reason) {
    case "balance":
      return `You hold ${held} ${sellSymbol}. Get test tokens on the Faucet page.`;
    case "allowance":
      return `Approve ${sellSymbol} first — the protocol cannot move your tokens yet.`;
    case "maker":
      return "Reconnect your wallet — the signed order does not match the connected account";
    case "email":
      return "This venue requires a verified email — sign out, verify your email, and sign in again";
    case "signature":
      return "Signature rejected by the gateway — re-quote and sign again";
    case "structure":
      return "The gateway rejected the order structure — re-quote and try again";
    default:
      return "Failed to submit intent. Please re-quote and try again.";
  }
}

/** Everything needed to submit, pinned before the wallet is ever touched. */
interface PreparedOrder {
  order: LopOrder;
  /** Extension bytes to submit unchanged; null on the legacy plain-order path. */
  extension: string | null;
  /** `eth_signTypedData_v4` payload, or null when the LOP address is unknown. */
  typedData: object | null;
}

/** Reported once per page load — a deployment either drafts or it does not. */
let legacyPathReported = false;

const DRAFT_MISMATCH =
  "This quote was drafted for another network or contract, so nothing was signed. Reload the page and quote again.";

/** Reported once per page load: a mismatch is the deployment's, not this trade's. */
let draftMismatchReported = false;

function chainIdOf(value: unknown): number | null {
  if (typeof value === "number") return Number.isSafeInteger(value) ? value : null;
  if (typeof value === "string" && /^0x[0-9a-f]+$/i.test(value)) return Number.parseInt(value, 16);
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value);
  return null;
}

/**
 * How a quote's draft would be signed, when that is not under the descriptor's
 * chain and LOP; null when it is, or when the quote carries no draft (the plain
 * order is signed under the descriptor's own domain).
 */
function draftDomainMismatch(quote: Quote): string | null {
  const draft = parseQuoteDraft(quote);
  if (draft === null) return null;
  const chainId = getChainId();
  const lop = getLopAddress();
  const { chainId: draftChain, verifyingContract } = draft.domain;
  const draftLop = typeof verifyingContract === "string" ? verifyingContract.toLowerCase() : null;
  if (chainIdOf(draftChain) === chainId && draftLop === lop) return null;
  return `draft chain ${String(draftChain)} and contract ${String(verifyingContract)}, descriptor chain ${String(chainId)} and LOP ${String(lop)}`;
}

function reportLegacyPath(reason: string | undefined): void {
  if (legacyPathReported) return;
  legacyPathReported = true;
  errorReporter.capture(
    `Quote carried no signable draft (${reason ?? "no draft in the quote response"}) — submitting a plain order without the settlement extension`,
    { level: "warning", meta: { source: "quote", path: "/api/v1/intent-quote" } },
  );
}

/** 256-bit random salt, for the legacy path where nothing binds the salt. */
function randomSalt(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let value = 0n;
  for (const byte of bytes) value = (value << 8n) | BigInt(byte);
  return value.toString(10);
}

/**
 * The quote's draft (the gateway's, from `@ailefx/order-sdk`: the extension
 * layout has one definition) with `maker` filled in and nothing else changed.
 * The salt's low 160 bits are `keccak256(extension)`, so any other edit breaks
 * the binding the maker signs; `maker` is in neither.
 *
 * A quote without a draft (no settlement configured) gets a plain 8-field order,
 * reported once: such an order can never collect a fee or run the settlement hook.
 */
function prepareOrder(
  quote: Quote,
  maker: string,
  legacy: { makerAsset: string; takerAsset: string; makingAmount: string; takingAmount: string },
): PreparedOrder | null {
  const draft = parseQuoteDraft(quote);
  if (draft !== null) {
    const order: LopOrder = {
      salt: draft.order.salt,
      maker,
      receiver: draft.order.receiver,
      makerAsset: draft.order.makerAsset,
      takerAsset: draft.order.takerAsset,
      makingAmount: draft.order.makingAmount,
      takingAmount: draft.order.takingAmount,
      makerTraits: draft.order.makerTraits,
    };
    return {
      order,
      extension: draft.extension,
      typedData: {
        domain: draft.domain,
        // Wallets require EIP712Domain in the payload; the gateway leaves it out.
        types: { EIP712Domain: EIP712_DOMAIN_TYPE, Order: draft.orderType },
        primaryType: draft.primaryType,
        message: order,
      },
    };
  }

  reportLegacyPath(quote.draftUnavailable);
  const order: LopOrder = {
    salt: randomSalt(),
    maker,
    receiver: maker,
    makerAsset: legacy.makerAsset,
    takerAsset: legacy.takerAsset,
    makingAmount: legacy.makingAmount,
    takingAmount: legacy.takingAmount,
    // No extension, so no traits: this is the pre-settlement order shape.
    makerTraits: "0",
  };
  return { order, extension: null, typedData: buildOrderTypedData(order) };
}

/** "100.00 EURC → 99.50 USDC" — used to show a price move in both prices. */
function legsText(sellBase: string, sellSymbol: string, buyBase: string, buySymbol: string): string {
  const sell = formatBaseUnits(sellBase, decimalsBySymbol(sellSymbol));
  const buy = formatBaseUnits(buyBase, decimalsBySymbol(buySymbol));
  return `${sell} ${sellSymbol} → ${buy} ${buySymbol}`;
}

/**
 * The amount received and its price, measured from the settlement, with the
 * quoted rate beside it only when they differ. With no indexed received amount
 * the price is left out: the signed floor is not what happened.
 */
function FillDetail({
  fill,
  sellSymbol,
  buySymbol,
  quotedRate,
}: {
  fill: OutcomeFill;
  sellSymbol: string;
  buySymbol: string;
  quotedRate: number;
}) {
  const buyDecimals = decimalsBySymbol(buySymbol);
  const rate = fill.executedRate;
  // Formatted strings, not floats: two rates that render alike are the same rate to the reader.
  const quotedText = formatRate(quotedRate);
  const filledText = rate === null ? null : formatRate(rate);
  const differs = filledText !== null && filledText !== quotedText;
  const better = differs && rate !== null && rate > quotedRate;

  return (
    <>
      {fill.receivedBase === null ? (
        <>
          {" "}
          sold {formatBaseUnits(fill.soldBase, decimalsBySymbol(sellSymbol))} {sellSymbol}
        </>
      ) : (
        <>
          {" "}
          you received {formatBaseUnits(fill.receivedBase, buyDecimals)} {buySymbol}
          {filledText !== null && (
            <>
              {" · "}1 {sellSymbol} = {filledText} {buySymbol}
            </>
          )}
        </>
      )}
      {fill.txHash !== null && (
        <>
          {" "}
          <TxLink txHash={fill.txHash} />
        </>
      )}
      {differs && (
        <div className="outcome-note">
          {better ? "Better than quoted — " : ""}
          quoted {quotedText}, filled {filledText}
        </div>
      )}
    </>
  );
}

/**
 * What became of the submitted intent, under the button. Nothing here reads as a
 * failure by itself: an intent that outlives the polling window is still live.
 */
function OutcomeBanner({
  outcome,
  orderHash,
  sellSymbol,
  buySymbol,
  quotedRate,
}: {
  outcome: OrderOutcome;
  orderHash: string;
  sellSymbol: string;
  buySymbol: string;
  quotedRate: number;
}) {
  const ordersLink = (label: string) => (
    <Link className="banner-link" to="/orders">
      {label}
    </Link>
  );

  if (outcome.kind === "filled") {
    return (
      <div className="inline-banner inline-banner-success" role="status">
        Filled —
        <FillDetail
          fill={outcome.fill}
          sellSymbol={sellSymbol}
          buySymbol={buySymbol}
          quotedRate={quotedRate}
        />
      </div>
    );
  }

  if (outcome.kind === "partial") {
    const { remainingBase } = outcome.fill;
    return (
      <div className="inline-banner inline-banner-success" role="status">
        Partially filled —
        <FillDetail
          fill={outcome.fill}
          sellSymbol={sellSymbol}
          buySymbol={buySymbol}
          quotedRate={quotedRate}
        />
        <div className="outcome-note">
          {remainingBase === null
            ? "The rest is still open."
            : `${formatBaseUnits(remainingBase, decimalsBySymbol(sellSymbol))} ${sellSymbol} still open.`}{" "}
          {ordersLink("Track it on the Orders page")}
        </div>
      </div>
    );
  }

  if (outcome.kind === "closed") {
    return (
      <div className="inline-banner inline-banner-warn" role="status">
        {outcome.status === "expired"
          ? "Intent expired — it is no longer live."
          : "Intent cancelled — it is no longer live."}{" "}
        {ordersLink("See the Orders page")}
      </div>
    );
  }

  if (outcome.kind === "unresolved") {
    // Not a warning: the intent is still on the book, only the watching stopped.
    return (
      <div className="inline-banner inline-banner-muted" role="status">
        Still open — {ordersLink("track it on the Orders page")}
      </div>
    );
  }

  // Taken, not yet mined: the wait is block time, so the copy names the transaction.
  if (outcome.kind === "filling") {
    return (
      <div className="inline-banner inline-banner-success" role="status">
        Intent submitted
        {orderHash ? ` · ${shortHash(orderHash)}` : ""}
        <div className="outcome-waiting">
          <span className="spinner" aria-hidden="true" />
          <span>
            Taken by a solver — settling on-chain{" "}
            {formatCountdown(Math.floor(outcome.elapsedMs / 1000))}{" "}
            <TxLink txHash={outcome.txHash} />
          </span>
        </div>
      </div>
    );
  }

  // Submitted, nothing settled yet.
  return (
    <div className="inline-banner inline-banner-success" role="status">
      Intent submitted
      {orderHash ? ` · ${shortHash(orderHash)}` : ""}
      <div className="outcome-waiting">
        <span className="spinner" aria-hidden="true" />
        <span>
          Waiting for a solver…{" "}
          {formatCountdown(
            Math.floor((outcome.kind === "pending" ? outcome.elapsedMs : 0) / 1000),
          )}
        </span>
      </div>
    </div>
  );
}

/** "EURC balance: 1,234.00" with the demo marker or a refresh; shared so both panels agree. */
function BalanceFoot({
  symbol,
  text,
  isDemo,
  onRefresh,
}: {
  symbol: string;
  text: string;
  isDemo: boolean;
  onRefresh?: () => void;
}) {
  return (
    <span className="panel-foot-balance">
      {symbol} balance: {text}
      {isDemo && <DemoChip />}
      {onRefresh !== undefined && (
        <button
          type="button"
          className="balance-refresh"
          onClick={onRefresh}
          aria-label="Refresh balances"
          title="Refresh balances"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 1.5v3h-3"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

export function TradeCard({ onOrderSubmitted, editing, onEditReplaced }: TradeCardProps) {
  // An edit's pair and amount are initial values only: the replacement is a fresh
  // trade. The default pair comes from the deployment's menu, never a constant.
  const markets = useMarkets();
  const [initialPair] = useState(defaultPair);
  const [sellToken, setSellToken] = useState(editing?.sellToken ?? initialPair.sell);
  const [buyToken, setBuyToken] = useState(editing?.buyToken ?? initialPair.buy);
  /** Human decimal input, may contain thousands separators. */
  const [sellAmount, setSellAmount] = useState(editing?.sellAmount ?? "");
  const [buyAmount, setBuyAmount] = useState("");
  /** The box the user last typed in — the one the quote is anchored to. */
  const [exactSide, setExactSide] = useState<ExactSide>("sell");
  const [swapRotations, setSwapRotations] = useState(0);
  /** The gateway folds it into the draft's `takingAmount`, so it is part of the signed order. */
  const [slippageBps, setSlippageBps] = useState(DEFAULT_SLIPPAGE_BPS);

  const [live, setLive] = useState<LiveQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  /** The next quote to fetch, debounced. Set by every input that moves. */
  const [request, setRequest] = useState<QuoteRequest | null>(null);
  /** Ticks once a second while a quote is live, so the countdown moves. */
  const [now, setNow] = useState(() => Date.now());

  const [flow, setFlow] = useState<FlowState>({ kind: "idle" });
  /** Driven off the success state alone, so every reset path also stops the polling. */
  const outcome = useOrderOutcome({
    orderHash: flow.kind === "success" ? flow.orderHash : null,
    sellSymbol: flow.kind === "success" ? flow.sellToken : sellToken,
    buySymbol: flow.kind === "success" ? flow.buyToken : buyToken,
  });
  /**
   * Exact by default: an unlimited allowance lets the protocol move every token
   * of this kind the wallet will ever hold, so it is only the user's choice.
   */
  const [approveUnlimited, setApproveUnlimited] = useState(false);
  /** Base-unit allowance of the sell token for the LOP; null = unknown. */
  const [allowance, setAllowance] = useState<bigint | null>(null);
  const [allowanceNonce, setAllowanceNonce] = useState(0);

  // Wallet session → real EIP-712 signing; demo session → zero signature.
  const walletAddress = getAddress();
  const busy =
    flow.kind === "cancelling" ||
    flow.kind === "approving" ||
    flow.kind === "signing" ||
    flow.kind === "checking" ||
    flow.kind === "submitting";

  // Name, colours and demo balances are local; decimals and address come from the descriptor.
  const sellInfo = tokenBySymbol(sellToken);
  const buyInfo = tokenBySymbol(buyToken);
  const sellDecimals = decimalsBySymbol(sellToken);
  const buyDecimals = decimalsBySymbol(buyToken);
  const sellAddress = tokenAddress(sellToken);
  const buyAddress = tokenAddress(buyToken);

  // A wallet session also needs the LOP (spender and legacy verifyingContract).
  // Any missing → refuse, never guess. The settlement address is inside the draft.
  const lopAddress = getLopAddress();
  const addressesUnavailable =
    sellAddress === null ||
    buyAddress === null ||
    (walletAddress !== null && (lopAddress === null || getChainId() === null));

  /** A leg the descriptor gives no decimals for has no amount: no quote, no signature. */
  const decimalsUnknown = sellDecimals === null || buyDecimals === null;

  // Only the side the user fixed has to be a valid amount — the other one is
  // whatever the last quote wrote there.
  const exactAmount = exactSide === "sell" ? sellAmount : buyAmount;
  const exactDecimals = exactSide === "sell" ? sellDecimals : buyDecimals;
  const amountValid = isPositiveAmount(exactAmount, exactDecimals);
  const fixedBase = toBaseUnits(exactAmount, exactDecimals);

  /** A quote may be swapped on only while it answers the question on screen. */
  const quoteUsable =
    marketAvailable(sellToken, buyToken) &&
    live !== null &&
    live.marketIdentity === marketIdentity(sellToken, buyToken) &&
    live.sellToken === sellToken &&
    live.buyToken === buyToken &&
    live.slippageBps === slippageBps &&
    (exactSide === "sell" ? live.sellBase : live.buyBase) === fixedBase;
  /** Seconds of freshness left. Derived, never stored: see the refresh effect. */
  const secondsLeft = live === null ? 0 : secondsUntil(live.quote.expiresAt, now);

  /** The sell leg for the allowance: the live quote's (on exact-out the only one), else typed. */
  const sellBase =
    live?.sellBase ?? (exactSide === "sell" ? toBaseUnits(sellAmount, sellDecimals) : null);

  /** A fiat leg makes the pair quotable but unsettleable. */
  const fiatLeg = isFiatLeg(sellToken) ? sellToken : isFiatLeg(buyToken) ? buyToken : null;

  // A demo session has no address to read, so it keeps the labelled mock numbers.
  const balances = useTokenBalances([sellToken, buyToken], walletAddress);

  /**
   * A wallet session shows the chain's number, "…" while loading, "—" on a failed
   * read, and never the mock: an empty wallet must not look like 181,000 EURC.
   */
  const balanceText = (symbol: string): string => {
    if (walletAddress === null) {
      return decimalsBySymbol(symbol) === null ? "—" : balanceFormatter.format(tokenBySymbol(symbol).mockBalance);
    }
    if (balances.loading) return "…";
    const value = balances.values[symbol];
    if (value === null || value === undefined) return "—";
    return formatBaseUnits(value.toString(), decimalsBySymbol(symbol));
  };

  /** For "you hold X"; an unknown balance reads "no", since the gateway said the wallet is short. */
  const heldSellBalanceText = walletAddress === null ? "—" : balanceText(sellToken);
  const heldSellBalance = /\d/.test(heldSellBalanceText) ? heldSellBalanceText : "no";

  /**
   * Base units behind a panel's balance, for the 20/50/MAX chips: the chain's
   * read alone for a wallet session (null hides the chips), the shown mock for a demo.
   */
  const balanceBase = (symbol: string): string | null => {
    const decimals = decimalsBySymbol(symbol);
    if (walletAddress === null) {
      return toBaseUnits(String(tokenBySymbol(symbol).mockBalance), decimals);
    }
    const value = balances.values[symbol];
    return value === null || value === undefined ? null : value.toString();
  };

  const sellBalanceBase = balanceBase(sellToken);

  /**
   * The floor from the gateway's draft, never `buyAmount × (1 − slippage)`, which
   * the signature does not contain. Without a draft there is no floor to show.
   */
  const minimumReceivedBase =
    live === null ? null : parseQuoteDraft(live.quote)?.order.takingAmount ?? null;

  /** Without the gateway's echo of the tolerance, the control must not show it as applied. */
  const slippageAcknowledged = live === null || typeof live.quote.slippageBps === "number";

  /** Only ever a dollar-pegged leg's quoted amount; both panels show it, being the same money. */
  const usdValue = ((): number | null => {
    if (!quoteUsable || live === null) return null;
    if (isUsdPegged(live.buyToken)) {
      const decimal = fromBaseUnits(live.buyBase, decimalsBySymbol(live.buyToken));
      return decimal === null ? null : Number(decimal);
    }
    if (isUsdPegged(live.sellToken)) {
      const decimal = fromBaseUnits(live.sellBase, decimalsBySymbol(live.sellToken));
      return decimal === null ? null : Number(decimal);
    }
    return null;
  })();
  const usdText = usdValue === null ? "" : `$${formatUsd(usdValue)}`;

  const sellLabel = "Sell";
  const submitLabel = "Swap";

  /**
   * `null` means unreadable and is never taken as zero, or a fiat leg would pin
   * the user behind an approve step that cannot work.
   */
  useEffect(() => {
    if (walletAddress === null || sellAddress === null || lopAddress === null) {
      setAllowance(null);
      return;
    }
    let cancelled = false;
    void readAllowance(sellAddress, walletAddress, lopAddress).then((value) => {
      if (!cancelled) setAllowance(value);
    });
    return () => {
      cancelled = true;
    };
  }, [walletAddress, sellAddress, lopAddress, allowanceNonce]);

  // The gateway is the second opinion: if it rejected the order for a short
  // allowance, approve first even when our own read said otherwise.
  const gatewaySaysApprove = flow.kind === "error" && flow.reason === "allowance";
  const allowanceShort =
    allowance !== null && sellBase !== null && allowance < BigInt(sellBase);
  const needsApproval =
    walletAddress !== null &&
    sellAddress !== null &&
    lopAddress !== null &&
    (allowanceShort || gatewaySaysApprove);

  /**
   * The debounce, the expiry refresh and the pre-submit check share this path.
   * A stale response is dropped: `seq` is the only ordering fetch gives.
   */
  const quoteSeq = useRef(0);
  const runQuote = useCallback(async (req: QuoteRequest): Promise<LiveQuote | null> => {
    if (!marketAvailable(req.sellToken, req.buyToken)) return null;
    if (decimalsBySymbol(req.sellToken) === null || decimalsBySymbol(req.buyToken) === null) return null;
    const identity = marketIdentity(req.sellToken, req.buyToken);
    const seq = ++quoteSeq.current;
    setQuoteLoading(true);
    setQuoteError(null);
    try {
      const q =
        req.side === "sell"
          ? await api.getQuote({
              sellToken: req.sellToken,
              buyToken: req.buyToken,
              sellAmount: req.base,
              slippageBps: req.slippageBps,
            })
          : await api.getQuote({
              sellToken: req.sellToken,
              buyToken: req.buyToken,
              buyAmount: req.base,
              slippageBps: req.slippageBps,
            });

      // Never rebuild the other leg from `rate`: float math would fabricate the signed number.
      const sellBaseOut = req.side === "sell" ? req.base : baseLeg(q.sellAmount);
      const buyBaseOut = req.side === "buy" ? req.base : baseLeg(q.buyAmount);
      if (sellBaseOut === null || buyBaseOut === null) {
        if (seq === quoteSeq.current) {
          setLive(null);
          setQuoteError("Quote incomplete — the gateway did not price both legs.");
        }
        errorReporter.capture("Quote response is missing a leg", {
          level: "error",
          meta: { source: "quote", side: req.side },
        });
        return null;
      }

      if (!marketAvailable(req.sellToken, req.buyToken) || identity !== marketIdentity(req.sellToken, req.buyToken)) return null;
      const landed: LiveQuote = {
        marketIdentity: identity,
        quote: q,
        sellToken: req.sellToken,
        buyToken: req.buyToken,
        side: req.side,
        sellBase: sellBaseOut,
        buyBase: buyBaseOut,
        slippageBps: req.slippageBps,
      };
      if (seq === quoteSeq.current) {
        setLive(landed);
        // Overwrite the derived box with the quoted number, full precision.
        if (req.side === "sell") {
          setBuyAmount(formatBaseUnitsExact(buyBaseOut, decimalsBySymbol(req.buyToken)));
        } else {
          setSellAmount(formatBaseUnitsExact(sellBaseOut, decimalsBySymbol(req.sellToken)));
        }
      }
      return landed;
    } catch (err) {
      // The previous quote stays up: a failed refresh warrants a warning, not blank numbers.
      if (seq === quoteSeq.current) {
        setQuoteError(
          err instanceof ApiError && err.status !== undefined
            ? `Quote failed (${err.status}) — adjust the amount to try again.`
            : "Quote failed — is the gateway running?",
        );
      }
      return null;
    } finally {
      if (seq === quoteSeq.current) setQuoteLoading(false);
    }
  }, []);

  /**
   * The only entry point for a quote: it refuses empty or zero input, and a fresh
   * object every time restarts the debounce.
   */
  const requestQuote = useCallback(
    (opts: {
      sellToken: string;
      buyToken: string;
      side: ExactSide;
      amount: string;
      slippageBps: number;
    }) => {
      if (!marketAvailable(opts.sellToken, opts.buyToken)) { setRequest(null); return; }
      const sellDecimals = decimalsBySymbol(opts.sellToken);
      const buyDecimals = decimalsBySymbol(opts.buyToken);
      const base = toBaseUnits(opts.amount, opts.side === "sell" ? sellDecimals : buyDecimals);
      if (sellDecimals === null || buyDecimals === null || base === null || base === "0") {
        setRequest(null);
        return;
      }
      setRequest({
        sellToken: opts.sellToken,
        buyToken: opts.buyToken,
        side: opts.side,
        base,
        slippageBps: opts.slippageBps,
      });
    },
    [],
  );

  useEffect(() => {
    if (!markets || busy) return;
    const available = marketAvailable(sellToken, buyToken);
    if (available && (live === null || live.marketIdentity === marketIdentity(sellToken, buyToken))) return;
    quoteSeq.current += 1;
    const next = available ? { sell: sellToken, buy: buyToken } : defaultPair();
    setSellToken(next.sell);
    setBuyToken(next.buy);
    setSellAmount("");
    setBuyAmount("");
    setRequest(null);
    setLive(null);
    setQuoteLoading(false);
  }, [markets, busy, sellToken, buyToken, live]);

  const quotedEditFor = useRef<string | null>(null);
  /** Order hash whose on-chain cancellation has already been mined and paid for. */
  const cancelledEdit = useRef<string | null>(null);
  useEffect(() => {
    if (editing === null || editing === undefined) return;
    if (quotedEditFor.current === editing.orderHash) return;
    quotedEditFor.current = editing.orderHash;
    if (editing.sellAmount === "") return;
    requestQuote({
      sellToken: editing.sellToken,
      buyToken: editing.buyToken,
      side: "sell",
      amount: editing.sellAmount,
      slippageBps,
    });
  }, [editing, requestQuote, slippageBps]);

  // Debounce: one quote per pause, never one per keystroke.
  useEffect(() => {
    if (request === null) return;
    const timer = setTimeout(() => {
      void runQuote(request);
    }, timing.quoteDebounceMs);
    return () => clearTimeout(timer);
  }, [request, runQuote]);

  // Freshness clock. Restarted with every quote so the countdown starts full.
  useEffect(() => {
    if (live === null) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), timing.tickMs);
    return () => clearInterval(timer);
  }, [live]);

  /** An expired quote refreshes itself, once per `expiresAt` whatever the clock does. */
  const refreshedFor = useRef<string | number | null>(null);
  useEffect(() => {
    if (live === null || secondsLeft > 0 || quoteLoading || busy) return;
    if (refreshedFor.current === live.quote.expiresAt) return;
    refreshedFor.current = live.quote.expiresAt;
    setRequest({
      sellToken: live.sellToken,
      buyToken: live.buyToken,
      side: live.side,
      base: live.side === "sell" ? live.sellBase : live.buyBase,
      // The card's current tolerance, the one on screen, not the expiring quote's.
      slippageBps,
    });
  }, [live, secondsLeft, quoteLoading, busy, slippageBps]);

  /** Anything the user changes voids the last verdict, never the input. */
  const resetFlow = () => {
    setFlow((f) => (f.kind === "idle" ? f : { kind: "idle" }));
  };

  /** The box the quote fills in is stale as soon as anything moves. */
  const clearDerived = (side: ExactSide) => {
    if (side === "sell") setBuyAmount("");
    else setSellAmount("");
  };

  /** A quote for another pair is not a stale quote, it is a wrong one. */
  const handleSellTokenChange = (symbol: string) => {
    const direction = markets?.directions.find((d) => d.sellToken === symbol && d.buyToken === buyToken)
      ?? markets?.directions.find((d) => d.sellToken === symbol);
    const nextBuy = markets ? direction?.buyToken ?? "" : symbol === buyToken ? sellToken : buyToken;
    setSellToken(symbol);
    setBuyToken(nextBuy);
    setLive(null);
    clearDerived(exactSide);
    resetFlow();
    requestQuote({
      sellToken: symbol,
      buyToken: nextBuy,
      side: exactSide,
      amount: exactSide === "sell" ? sellAmount : buyAmount,
      slippageBps,
    });
  };

  const handleBuyTokenChange = (symbol: string) => {
    const direction = markets?.directions.find((d) => d.sellToken === sellToken && d.buyToken === symbol)
      ?? markets?.directions.find((d) => d.buyToken === symbol);
    const nextSell = markets ? direction?.sellToken ?? "" : symbol === sellToken ? buyToken : sellToken;
    setBuyToken(symbol);
    setSellToken(nextSell);
    setLive(null);
    clearDerived(exactSide);
    resetFlow();
    requestQuote({
      sellToken: nextSell,
      buyToken: symbol,
      side: exactSide,
      amount: exactSide === "sell" ? sellAmount : buyAmount,
      slippageBps,
    });
  };

  const handleSwapDirection = () => {
    if (!marketAvailable(buyToken, sellToken)) return;
    setSwapRotations((r) => r + 1);
    const nextSell = buyToken;
    const nextBuy = sellToken;
    setSellToken(nextSell);
    setBuyToken(nextBuy);
    // The typed number keeps its box and side; the other box quoted the old pair, so it goes.
    setLive(null);
    clearDerived(exactSide);
    resetFlow();
    requestQuote({
      sellToken: nextSell,
      buyToken: nextBuy,
      side: exactSide,
      amount: exactSide === "sell" ? sellAmount : buyAmount,
      slippageBps,
    });
  };

  const handleAmountChange = (side: ExactSide, value: string) => {
    // Digits, one dot, thousands separators — everything else is ignored.
    if (value !== "" && !AMOUNT_PATTERN.test(value)) return;
    // Typing anchors the quote to this side and voids the counterpart.
    setExactSide(side);
    if (side === "sell") setSellAmount(value);
    else setBuyAmount(value);
    clearDerived(side);
    resetFlow();
    requestQuote({ sellToken, buyToken, side, amount: value, slippageBps });
  };

  /** A new tolerance is a new order, so a new quote; the amount, pair and side stay. */
  const handleSlippageChange = (bps: number) => {
    if (bps === slippageBps) return;
    setSlippageBps(bps);
    resetFlow();
    requestQuote({
      sellToken,
      buyToken,
      side: exactSide,
      amount: exactSide === "sell" ? sellAmount : buyAmount,
      slippageBps: bps,
    });
  };

  /** BigInt on the base-unit balance, then the typing path, so a chip acts like a keystroke. */
  const handleAmountShortcut = (percent: number) => {
    if (sellBalanceBase === null) return;
    const picked = (BigInt(sellBalanceBase) * BigInt(percent)) / 100n;
    handleAmountChange("sell", formatBaseUnitsExact(picked.toString(), sellDecimals));
  };

  /** Focusing the derived box fixes that side; nothing is re-quoted until the user types. */
  const handleAmountFocus = (side: ExactSide) => {
    setExactSide(side);
  };

  /**
   * Approve if short, sign, re-price if the quote expired in the wallet, submit.
   * The quote is captured up front: it may refresh underneath the signed order.
   */
  const handleSwap = async () => {
    if (busy || !quoteUsable || live === null || fiatLeg !== null || decimalsUnknown) return;
    if (markets) {
      setFlow({ kind: "checking" });
      await loadRuntimeConfig(true);
      if (!marketAvailable(sellToken, buyToken) || live.marketIdentity !== marketIdentity(sellToken, buyToken)) {
        setFlow({ kind: "error", reason: "generic", message: "This pair is no longer available. Choose another pair." });
        return;
      }
    }
    if (sellAddress === null || buyAddress === null) {
      setFlow({ kind: "unavailable" });
      return;
    }
    const signedQuote = live;
    // Before anything reaches the wallet: a draft signed under another domain
    // would be an order for another chain or contract.
    if (walletAddress !== null) {
      const mismatch = draftDomainMismatch(signedQuote.quote);
      if (mismatch !== null) {
        if (!draftMismatchReported) {
          draftMismatchReported = true;
          errorReporter.capture(`Quote draft refused before signing: ${mismatch}`, {
            level: "error",
            meta: { source: "quote", path: "/api/v1/intent-quote" },
          });
        }
        setFlow({ kind: "error", reason: "generic", message: DRAFT_MISMATCH });
        return;
      }
    }
    // The gateway rejects a maker other than the connected address on wallet sessions.
    const maker = walletAddress ?? DEMO_MAKER;

    // --- 0. the order being replaced ---------------------------------------
    // The cancel goes first and its failure stops the submit: two live orders for
    // one intent could both fill. `cancelledEdit` lets a retry skip a mined cancel.
    if (editing != null && cancelledEdit.current !== editing.orderHash) {
      setFlow({ kind: "cancelling", hash: null });
      try {
        await cancelIntent({
          orderHash: editing.orderHash,
          makerTraits: editing.makerTraits,
          maker: walletAddress,
          onStage: (stage) => {
            if (stage.kind === "mining") setFlow({ kind: "cancelling", hash: stage.txHash });
          },
        });
        cancelledEdit.current = editing.orderHash;
      } catch (err) {
        if (isUserRejected(err)) {
          // Expected, not reported: the original stays live and nothing new is signed.
          setFlow({ kind: "rejected", step: "cancel" });
          return;
        }
        const message = err instanceof CancelError ? err.message : explainWalletError(err);
        errorReporter.capture(
          `Cancelling the edited intent failed: ${
            err instanceof CancelError ? err.message : toWalletError(err).message
          }`,
          { level: "error", meta: { source: "cancel", orderHash: editing.orderHash } },
        );
        setFlow({ kind: "error", reason: "generic", message });
        return;
      }
    }

    // --- 1. allowance ------------------------------------------------------
    // The protocol pulls the sell token from the maker's wallet, so approve before signing.
    if (needsApproval) {
      if (walletAddress === null || lopAddress === null) {
        setFlow({ kind: "unavailable" });
        return;
      }
      setFlow({ kind: "approving", hash: null });
      try {
        const amount = approveUnlimited ? MAX_UINT256 : BigInt(signedQuote.sellBase);
        const hash = await approve(walletAddress, sellAddress, lopAddress, amount);
        setFlow({ kind: "approving", hash });
        const receipt = await waitForReceipt(hash);
        if (isReverted(receipt)) {
          setFlow({
            kind: "error",
            reason: "generic",
            message: "Approval reverted on chain — try again",
          });
          return;
        }
        setAllowanceNonce((n) => n + 1);
      } catch (err) {
        if (isUserRejected(err)) {
          // Expected outcome — the user declined in their wallet. Not reported.
          setFlow({ kind: "rejected", step: "approve" });
          return;
        }
        const walletError = toWalletError(err);
        errorReporter.capture(`Token approval failed: ${walletError.message}`, {
          level: "error",
          meta: { source: "wallet", code: walletError.code },
        });
        setFlow({ kind: "error", reason: "generic", message: explainWalletError(err) });
        return;
      }
    }

    // --- 2. the order ------------------------------------------------------
    const prepared = prepareOrder(signedQuote.quote, maker, {
      makerAsset: sellAddress,
      takerAsset: buyAddress,
      makingAmount: signedQuote.sellBase,
      takingAmount: signedQuote.buyBase,
    });
    if (prepared === null) {
      setFlow({ kind: "unavailable" });
      return;
    }

    // --- 3. signature ------------------------------------------------------
    if (markets) {
      await loadRuntimeConfig(true);
      if (!marketAvailable(signedQuote.sellToken, signedQuote.buyToken) || signedQuote.marketIdentity !== marketIdentity(signedQuote.sellToken, signedQuote.buyToken)) {
        setFlow({ kind: "error", reason: "generic", message: "This pair is no longer available. Choose another pair." });
        return;
      }
    }
    let signature = DEMO_SIGNATURE;
    if (walletAddress !== null) {
      // The legacy path needs the LOP as verifyingContract: refuse rather than guess.
      if (prepared.typedData === null) {
        setFlow({ kind: "unavailable" });
        return;
      }
      setFlow({ kind: "signing" });
      try {
        signature = await signTypedData(walletAddress, prepared.typedData);
      } catch (err) {
        if (isUserRejected(err)) {
          // Expected outcome — the user declined in their wallet. Not reported.
          setFlow({ kind: "rejected", step: "sign" });
          return;
        }
        const walletError = toWalletError(err);
        errorReporter.capture(`Order signing failed: ${walletError.message}`, {
          level: "error",
          meta: { source: "wallet", code: walletError.code },
        });
        setFlow({
          kind: "error",
          reason: "generic",
          message: "Could not sign the intent. Please try again.",
        });
        return;
      }
    }

    // --- 4. did the price move while the wallet was open? ------------------
    // An expired quote is re-priced and compared: a floor the user never saw is not sent.
    if (secondsUntil(signedQuote.quote.expiresAt) <= 0) {
      setFlow({ kind: "checking" });
      const fresh = await runQuote({
        sellToken: signedQuote.sellToken,
        buyToken: signedQuote.buyToken,
        side: signedQuote.side,
        base: signedQuote.side === "sell" ? signedQuote.sellBase : signedQuote.buyBase,
        // The signed tolerance, or the comparison reports a move that never happened.
        slippageBps: signedQuote.slippageBps,
      });
      if (
        fresh !== null &&
        (fresh.sellBase !== signedQuote.sellBase || fresh.buyBase !== signedQuote.buyBase)
      ) {
        setFlow({
          kind: "priceMoved",
          signed: legsText(
            signedQuote.sellBase,
            signedQuote.sellToken,
            signedQuote.buyBase,
            signedQuote.buyToken,
          ),
          fresh: legsText(fresh.sellBase, fresh.sellToken, fresh.buyBase, fresh.buyToken),
        });
        return;
      }
    }

    // --- 5. submit ---------------------------------------------------------
    setFlow({ kind: "submitting" });
    try {
      const record = await api.submitOrder({
        order: prepared.order,
        signature,
        ...(prepared.extension === null ? {} : { extension: prepared.extension }),
      });
      setFlow({
        kind: "success",
        orderHash: record.orderHash ?? "",
        sellToken: signedQuote.sellToken,
        buyToken: signedQuote.buyToken,
        quotedRate: signedQuote.quote.rate,
      });
      // The boxes empty: a live quote left up invites a second click on a finished trade.
      setLive(null);
      setRequest(null);
      setSellAmount("");
      setBuyAmount("");
      balances.refresh();
      setAllowanceNonce((n) => n + 1);
      onEditReplaced?.();
      onOrderSubmitted();
    } catch (err) {
      // Already reported by the api layer; here we only translate it.
      const reason = classifySubmitFailure(err);
      const detail = err instanceof ApiError ? err.detail : undefined;
      setFlow({
        kind: "error",
        reason,
        message: submitFailureMessage(reason, sellToken, heldSellBalance),
        ...(detail === undefined ? {} : { detail }),
      });
    }
  };

  const swapLabel =
    flow.kind === "cancelling"
      ? "Cancelling the original…"
      : flow.kind === "approving"
      ? `Approving ${sellToken}…`
      : flow.kind === "signing"
        ? "Confirm in wallet…"
        : flow.kind === "checking"
          ? "Checking price…"
          : flow.kind === "submitting"
            ? "Submitting…"
            : quoteLoading && !quoteUsable
              ? "Fetching quote…"
              : submitLabel;

  /**
   * Checked before signing too: an order refused after signing looks live, and
   * one nobody will fill looks like one nobody has reached yet.
   */
  const maxTicketUsd = getMaxTicketUsd();
  const overTicketLimit = maxTicketUsd > 0 && usdValue !== null && usdValue > maxTicketUsd;

  const swapDisabled =
    busy ||
    !amountValid ||
    !quoteUsable ||
    fiatLeg !== null ||
    decimalsUnknown ||
    addressesUnavailable ||
    overTicketLimit;

  return (
    <section className="trade-widget" aria-label="Trade">
      {/* One trade, one name: the head holds only the slippage gear. */}
      {markets && markets.directions.length === 0 && <p role="status">No pairs are ready to swap on this network yet.</p>}
      <div className="card-head card-head-single">
        <SlippageControl
          valueBps={slippageBps}
          activeBps={slippageAcknowledged ? slippageBps : null}
          onChange={handleSlippageChange}
          disabled={busy}
        />
      </div>

      {/* Said before anything is signed: an edit costs a cancellation and a new signature. */}
      {editing != null && (
        <div className="inline-banner inline-banner-warn" role="status">
          Editing intent {shortHash(editing.orderHash)} — submitting will cancel the original and
          sign a new one.
        </div>
      )}

      {/* SELL — editable: typing here means "spend exactly this much". */}
      <div className="trade-panel trade-panel-sell">
        <div className="panel-head">
          <span className="panel-tag panel-tag-sell">{sellLabel}</span>
          {walletAddress !== null && (
            <span className="panel-address" title={walletAddress}>
              {shortHash(walletAddress)}
            </span>
          )}
        </div>
        <div className="panel-main">
          <input
            className={exactSide === "sell" ? "amount-display" : "amount-display amount-derived"}
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={sellAmount}
            onChange={(e) => handleAmountChange("sell", e.target.value)}
            onFocus={() => handleAmountFocus("sell")}
            aria-label={`${sellLabel} amount`}
            {...(exactSide === "sell"
              ? {}
              : { title: "Quoted amount — click to enter it yourself" })}
          />
          <TokenSelect
            value={sellToken}
            onChange={handleSellTokenChange}
            label={`${sellLabel} token`}
            symbols={markets?.directions.map((d) => d.sellToken)}
          />
        </div>
        <div className="panel-foot">
          <span className="panel-usd">{usdText}</span>
          <span className="panel-foot-right">
            <BalanceFoot
              symbol={sellInfo.symbol}
              text={balanceText(sellInfo.symbol)}
              isDemo={walletAddress === null}
              {...(walletAddress === null ? {} : { onRefresh: balances.refresh })}
            />
            {sellBalanceBase !== null && (
              <AmountShortcuts onPick={handleAmountShortcut} disabled={busy} />
            )}
          </span>
        </div>
      </div>

      <div className="swap-seam">
        <button
          type="button"
          className="swap-button"
          onClick={handleSwapDirection}
          disabled={busy || !marketAvailable(buyToken, sellToken)}
          aria-label="Swap direction"
          title="Swap direction"
          style={{ transform: `rotate(${swapRotations * 180}deg)` }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path
              d="M6 3v10M6 13l-3-3M6 13l3-3M12 15V5M12 5l-3 3M12 5l3 3"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* BUY — editable: typing here means "give me exactly this much". */}
      <div className="trade-panel trade-panel-buy">
        <div className="panel-head">
          <span className="panel-tag panel-tag-buy">Buy</span>
        </div>
        <div className="panel-main">
          <input
            className={exactSide === "buy" ? "amount-display" : "amount-display amount-derived"}
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={buyAmount}
            onChange={(e) => handleAmountChange("buy", e.target.value)}
            onFocus={() => handleAmountFocus("buy")}
            aria-label="Buy amount"
            {...(exactSide === "buy" ? {} : { title: "Quoted amount — click to enter it yourself" })}
          />
          <TokenSelect value={buyToken} onChange={handleBuyTokenChange} label="Buy token" symbols={markets?.directions.filter((d) => d.sellToken === sellToken).map((d) => d.buyToken)} />
        </div>
        <div className="panel-foot">
          <span className="panel-usd">{usdText}</span>
          <span className="panel-foot-right">
            <BalanceFoot
              symbol={buyInfo.symbol}
              text={balanceText(buyInfo.symbol)}
              isDemo={walletAddress === null}
            />
          </span>
        </div>
      </div>

      {/* The signed floor, from the draft; no draft, no line. */}
      {quoteUsable && live !== null && minimumReceivedBase !== null && (
        <div className="min-received">
          <span className="min-received-label">Minimum received</span>
          <span className="min-received-value">
            {formatBaseUnits(minimumReceivedBase, decimalsBySymbol(live.buyToken))}{" "}
            {live.buyToken}
          </span>
        </div>
      )}

      {/* Muted, not blanked, while refreshing: blank numbers would look like a failure. */}
      {live !== null && (
        <div
          className={quoteLoading ? "quote-info quote-info-stale" : "quote-info"}
          role="status"
        >
          <span className="quote-rate">
            1 {live.sellToken} = {formatRate(live.quote.rate)} {live.buyToken}
          </span>
          <span className="quote-countdown">
            {quoteLoading ? "Refreshing…" : `Refreshes in ${formatCountdown(secondsLeft)}`}
          </span>
        </div>
      )}

      {/* One button, one trade. It approves, signs and submits by itself. */}
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => void handleSwap()}
        disabled={swapDisabled}
      >
        {swapLabel}
      </button>

      {overTicketLimit && (
        <div className="inline-banner inline-banner-warn" role="status">
          Maximum ${formatUsd(maxTicketUsd)} per swap — reduce the amount, or split it into
          several intents.
        </div>
      )}

      {/* Shown before any quote, so the user learns it while choosing the pair. */}
      {fiatLeg !== null && (
        <div className="inline-banner inline-banner-warn" role="status">
          {fiatLegExplanation(fiatLeg)}
        </div>
      )}

      {/* Before the swap runs, because the swap sends the approval. */}
      {needsApproval && fiatLeg === null && (
        <label className="approve-scope">
          <input
            type="checkbox"
            checked={approveUnlimited}
            onChange={(e) => setApproveUnlimited(e.target.checked)}
            disabled={busy}
          />
          <span>
            Approve unlimited {sellToken}{" "}
            <span className="approve-scope-hint">
              (skips the approval on future trades; the protocol may then move
              any amount of your {sellToken})
            </span>
          </span>
        </label>
      )}

      {flow.kind === "cancelling" && flow.hash !== null && (
        <div className="inline-banner inline-banner-muted" role="status">
          Cancellation sent · <InlineTxLink txHash={flow.hash} /> — the new intent is signed once it
          confirms
        </div>
      )}

      {flow.kind === "approving" && flow.hash !== null && (
        <div className="inline-banner inline-banner-muted" role="status">
          Approval sent · <InlineTxLink txHash={flow.hash} /> — waiting for confirmation
        </div>
      )}

      {quoteError !== null && (
        <div className="inline-banner inline-banner-error" role="alert">
          {quoteError}
        </div>
      )}
      {flow.kind === "rejected" && (
        <div className="inline-banner inline-banner-warn" role="alert">
          {flow.step === "approve"
            ? "Approval rejected — try again"
            : flow.step === "cancel"
              ? "Cancellation rejected — the original intent is untouched and nothing new was signed"
              : "Signature rejected — try again"}
        </div>
      )}
      {flow.kind === "priceMoved" && (
        <div className="inline-banner inline-banner-warn" role="alert">
          Price moved — review and swap again. You signed {flow.signed}; the market is now{" "}
          {flow.fresh}.
        </div>
      )}
      {(flow.kind === "unavailable" || addressesUnavailable) && (
        <div className="inline-banner inline-banner-warn" role="alert">
          {ADDRESS_UNAVAILABLE}
        </div>
      )}
      {flow.kind === "error" && (
        // The gateway's `detail` stays in `title`, so support can recover the exact reason.
        <div
          className="inline-banner inline-banner-error"
          role="alert"
          {...(flow.detail === undefined ? {} : { title: flow.detail })}
        >
          {flow.message}
          {flow.reason === "balance" && walletAddress !== null && (
            <>
              {" "}
              <Link className="banner-link" to={`/faucet?address=${walletAddress}`}>
                Open the faucet
              </Link>
            </>
          )}
        </div>
      )}
      {flow.kind === "success" && (
        <OutcomeBanner
          outcome={outcome}
          orderHash={flow.orderHash}
          sellSymbol={flow.sellToken}
          buySymbol={flow.buyToken}
          quotedRate={flow.quotedRate}
        />
      )}
    </section>
  );
}
